<template>
  <auth-card
    :title="$t('pages.login.admin.welcome')"
    :subtitle="$t('pages.login.admin.subtitle')"
  >
    <b-form @submit.prevent="authAdminUser">
      <b-form-group
        id="username-group"
        :label="$t('auth.login')"
        label-for="username"
        :invalid-feedback="invalidCredentials"
        :state="stateUsername"
      >
        <b-form-input
          id="username"
          v-model="username"
          type="text"
          autocomplete="username"
          :state="stateUsernameInput"
          :placeholder="$t('auth.field.username')"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        id="password-group"
        :label="$t('auth.password')"
        label-for="password"
        :invalid-feedback="invalidPassword"
        :state="statePassword"
      >
        <password-input
          id="password"
          v-model="password"
          @blur="invalidCredentials = null"
          :state="statePasswordInput"
          :placeholder="$t('auth.placeholder_password')"
        />
      </b-form-group>
      <vue-recaptcha
        v-if="useRecaptcha"
        ref="recaptcha"
        @expired="onCaptchaExpired"
        @verify="onCaptchaVerified"
        @error="onRecaptchaError"
        :sitekey="recaptchaSiteKey"
        size="invisible" />
      <b-button
        block
        type="submit"
        :disabled="loading"
        variant="primary">
        {{ $t('auth.login_button') }}
      </b-button>
    </b-form>
    <p class="text-center text-muted mt-3 mb-0">
      {{ $t('auth.forgot_password') }}
      <router-link :to="{ name: 'admin-reset-password' }">
        {{ $t('auth.reset_password') }}
      </router-link>
    </p>
  </auth-card>
</template>

<script>
  import VueRecaptcha from 'vue-recaptcha'
  import { RECAPTCHA_SITE_KEY, ADMIN_SECTION_PATH, USE_RECAPTCHA } from '@base/configs'
  import AuthCard from '@anonymous/components/AuthCard.vue'
  import PasswordInput from '@base/components/PasswordInput.vue'
  import { required } from 'vuelidate/lib/validators'

  export default {
    name: 'AdminLogin',

    components: { AuthCard, PasswordInput, VueRecaptcha },

    data () {
      return {
        username: null,
        password: null,
        invalidCredentials: null,
        useRecaptcha: USE_RECAPTCHA,
        recaptchaToken: null,
        recaptchaSiteKey: RECAPTCHA_SITE_KEY,
        recaptchaError: false,
        loading: false,
      }
    },

    watch: {
      username () {
        this.invalidCredentials = null
      },
      password () {
        this.invalidCredentials = null
      },
    },

    computed: {
      payloads () {
        return {
          username: this.username,
          password: this.password,
          grecaptcha_token: this.useRecaptcha ? this.recaptchaToken: undefined,
        }
      },
      stateUsernameInput () {
        return this.invalidCredentials !== null ? false : null
      },
      stateUsername () {
        return this.invalidCredentials === null
      },
      statePasswordInput () {
        return (this.$v.password.$error || this.invalidCredentials !== null) ? false : null
      },
      statePassword () {
        return !this.$v.password.$error || this.invalidCredentials !== null
      },
      invalidPassword () {
        if (this.$v.password.$error) {
          if (!this.$v.password.required)
            return this.$t('validation.required', { field: this.$t('auth.field.password') })
        }
        return null
      },
    },

    validations: {
      username: {
        required,
      },
      password: {
        required
      },
    },

    methods: {
      onRecaptchaError () {
        this.recaptchaError = true
      },

      onCaptchaExpired () {
        if (this.useRecaptcha)
          this.$refs.recaptcha.reset()
      },

      authAdminUser () {
        this.$v.$touch()
        if (this.useRecaptcha) {
          if (this.recaptchaError) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: this.$t('notifications.title.error'),
              text: this.$t('error.recaptcha')
            })
            return
          }
        }
        if (!this.$v.$invalid) {
          if (this.useRecaptcha)
            this.$refs.recaptcha.execute()
          else
            this.onCaptchaVerified()
        }
      },

      onCaptchaVerified (recaptchaToken) {
        this.$v.$reset()
        this.invalidCredentials = null
        if (this.useRecaptcha) {
          this.recaptchaToken = recaptchaToken
          this.$refs.recaptcha.reset()
        }
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.dispatch(this.$_actionTypes.LOGIN_USER, this.payloads)
          .then((response) => {
            if (response.isAdmin) {
              window.location.replace(ADMIN_SECTION_PATH)
            } else {
              this.$notify({
                group: 'app',
                type: 'warn',
                title: this.$t('notifications.title.warning'),
                text: this.$t('error.permission_denied')
              })
            }
          })
          .catch((error) => {
            this.invalidCredentials = error.data.error
            this.$notify({
              group: 'app',
              type: 'error',
              title: this.$t('notifications.title.error'),
              text: error.data.error
            })
          })
          .finally(() => {
            this.loading = false
            this.loader.hide()
          })
      }
    }
  }
</script>

<style>

</style>
